/** What a lap is compared against, and what that comparison is drawn in.
 *
 * "Compare with" is the one place in the portal where one account's driving is
 * shown to another, and the rule behind it is the service's, not the screen's:
 * `GET /api/sessions/public` answers with the drivers who marked a session on
 * this layout public, one row each, and nobody else exists as far as this
 * screen is concerned. There is no way to ask for a private session, because
 * there is no endpoint that would answer.
 *
 * The colours are the design's and they are a hierarchy rather than a palette.
 * Your own best lap is neutral — it is the baseline, not a rival. Another
 * driver gets a semantic hue so that a line on the map is identifiable at a
 * glance without a key. The circuit's best is the brightest neutral, because
 * it is the thing to beat and it belongs to nobody in particular.
 *
 * The list is capped. The endpoint answers up to fifty drivers and a
 * segmented control with fifty options is not a segmented control, so the
 * quickest few are offered and the rest are simply not in the row — the same
 * rule the header uses for links: what is not yours is not shown, rather than
 * shown and unusable.
 */

import type { PublicDriver } from '@/lib/types';

/** How many other drivers fit in the toolbar before it stops being one. */
export const MAX_DRIVERS = 3;

export type ReferenceKind = 'my_best' | 'driver' | 'track_best';

export interface Reference {
  /** Stable across a re-fetch, so a chosen driver stays chosen. */
  id: string;
  kind: ReferenceKind;
  label: string;
  /** The lap time being compared against, when it is known up front. */
  timeMs: number | null;
  /** The stroke this reference's line and legend are drawn in. */
  colour: string;
  /** Whose public session to read the reference lap out of. Absent for your
   *  own best, which is in the session already on screen. */
  driver?: PublicDriver;
}

/** A driver's hue. Two are enough for the design's own example and the cycle
 *  keeps a third from colliding with either. */
const DRIVER_COLOURS = ['var(--color-warn)', 'var(--color-ok)'] as const;

export function driverColour(index: number): string {
  return DRIVER_COLOURS[index % DRIVER_COLOURS.length] as string;
}

/**
 * The row of options, in the order the design puts them: your own baseline,
 * the drivers who published a lap here, and the quickest lap on the circuit.
 *
 * "Track best" is only offered when there is somebody else to be quicker than.
 * With no public drivers it would resolve to your own best lap in a different
 * colour, which is a second button for the same line.
 */
export function referenceOptions(
  myBestMs: number | null,
  drivers: readonly PublicDriver[],
): Reference[] {
  const options: Reference[] = [
    {
      id: 'my_best',
      kind: 'my_best',
      label: 'my best',
      timeMs: myBestMs,
      colour: 'var(--color-neutral-500)',
    },
  ];

  const offered = [...drivers]
    .sort((a, b) => a.best_lap_ms - b.best_lap_ms)
    .slice(0, MAX_DRIVERS);

  offered.forEach((driver, index) => {
    options.push({
      id: `driver:${driver.user_id}`,
      kind: 'driver',
      label: driver.display_name,
      timeMs: driver.best_lap_ms,
      colour: driverColour(index),
      driver,
    });
  });

  const quickest = offered[0];
  if (quickest) {
    // The circuit's best is whichever is quicker: the fastest published lap,
    // or your own. Pointing it at your session when you hold it is the true
    // answer, and it keeps the button from lying about who is ahead.
    const mineIsBest = myBestMs !== null && myBestMs <= quickest.best_lap_ms;
    options.push({
      id: 'track_best',
      kind: 'track_best',
      label: 'track best',
      timeMs: mineIsBest ? myBestMs : quickest.best_lap_ms,
      colour: 'var(--color-neutral-200)',
      ...(mineIsBest ? {} : { driver: quickest }),
    });
  }

  return options;
}

/** The chosen option, falling back to your own best when the previous choice
 *  is no longer on offer — a circuit changed, or a driver went private. */
export function resolveReference(options: readonly Reference[], id: string): Reference | null {
  return options.find((option) => option.id === id) ?? options[0] ?? null;
}
